// One factory for all query keys — no string literals in hooks.
// Keys nested under farm() cascade on farm-level invalidation;
// metricEvents uses its own top-level root because its query shape doesn't map cleanly
// to the farm hierarchy.
export const queryKeys = {
  // farms intentionally excludes the user from the key: the '/hubId' claim is
  // session-constant (set by Auth0 at login, not mutable without a full app
  // remount). Scoping by user would create a different cache entry per Auth0
  // user object identity without any practical cache benefit.
  farms: () => ['farms'] as const,
  // Hub-rooted, not farm-rooted: the hub is what scopes the user list, and it outlives any
  // farm selection (farms precedent above).
  hubUsers: (hubId: string | undefined) => ['hubUsers', hubId] as const,
  // string | undefined: callers must gate with `enabled: !!farmId` so queries
  // never fire with undefined in the key.
  farm: (farmId: string | undefined) => ['farms', farmId] as const,
  farmClusters: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'clusters'] as const,
  // Bust all findings (lists + details) with queryKeys.findingsRoot.
  findingsRoot: ['findings'] as const,
  // Full identity includes namespace + resolved because the API response varies on both.
  findings: (dcsId: string | undefined, namespace?: string, resolved?: boolean) =>
    [...queryKeys.findingsRoot, 'by-dcs', dcsId, namespace ?? null, resolved ?? null] as const,
  // finding is a detail key — kept under a 'detail' segment to prevent aliasing 'by-dcs'.
  finding: (findingId: string | undefined) =>
    [...queryKeys.findingsRoot, 'detail', findingId] as const,
  findingComments: (findingId: string | undefined) =>
    [...queryKeys.finding(findingId), 'comments'] as const,
  // Bust all documents with queryKeys.documentsRoot.
  documentsRoot: ['documents'] as const,
  // entityTypeId and types are part of the response identity — both included in the key.
  // types is sorted for a stable cache identity regardless of call-site array order.
  documents: (entityId: string | undefined, entityTypeId?: number, types?: number[]) =>
    [
      ...queryKeys.documentsRoot,
      entityId,
      entityTypeId ?? null,
      types ? [...types].sort((a, b) => a - b) : null,
    ] as const,
  // userRole is part of both identities: the API computes actionsAllowed for the requesting role.
  dataCollectionStatementsRoot: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'dataCollectionStatements'] as const,
  dataCollectionStatements: (farmId: string | undefined, userRole: string | undefined) =>
    [...queryKeys.dataCollectionStatementsRoot(farmId), 'list', userRole] as const,
  dataCollectionStatement: (
    farmId: string | undefined,
    year: string | number | undefined,
    userRole?: string,
  ) => [...queryKeys.dataCollectionStatementsRoot(farmId), 'by-year', year, userRole] as const,
  // Bust every metric-event read (any farm/namespace/year query) with queryKeys.metricEventsRoot.
  metricEventsRoot: ['metricEvents'] as const,
  metricEvents: (query: {
    farmId?: string
    namespace?: string
    year?: number
    paddockIds?: string[]
  }) => [...queryKeys.metricEventsRoot, query] as const,
  program: (programId: string | number | undefined) => ['programs', programId] as const,
  programConfig: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'programConfig'] as const,
  monitoringSites: (farmId: string | undefined, options?: { monitoringOnly?: boolean }) =>
    options
      ? ([...queryKeys.farm(farmId), 'monitoringSites', options] as const)
      : ([...queryKeys.farm(farmId), 'monitoringSites'] as const),
  monitoringEvents: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'monitoringEvents'] as const,
  // monitoringTasks/monitoringActivities sit outside the farm hierarchy: they are keyed by an
  // arbitrary ApiV2QueryType object (not a bare farmId), matching the metricEvents precedent above.
  // Bust every cached lookup of either with its root.
  monitoringTasksRoot: ['monitoringTasks'] as const,
  monitoringTasks: (query: unknown) => [...queryKeys.monitoringTasksRoot, query] as const,
  monitoringActivitiesRoot: ['monitoringActivities'] as const,
  monitoringActivities: (query: unknown) => [...queryKeys.monitoringActivitiesRoot, query] as const,
  paddocks: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'paddocks'] as const,
  // Intentionally outside the farm hierarchy: the by-ids read has no farmId in scope at its call
  // sites, so farm-level invalidation cannot cascade here — every paddock write must bust this
  // root by name.
  paddocksByIdsRoot: ['paddocks', 'by-ids'] as const,
  paddocksByIds: (ids: string[] | undefined) =>
    [...queryKeys.paddocksByIdsRoot, ids ? [...ids].sort() : null] as const,
  // `year ?? null` is IN the farmSubdivisions key, so the no-year key is an exact entry rather than
  // a prefix of the year-scoped ones — busting every year needs this root.
  farmSubdivisionsRoot: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'farmSubdivisions'] as const,
  // year is part of the response identity: the v1 list filters server-side on it.
  farmSubdivisions: (farmId: string | undefined, year?: string | number) =>
    [...queryKeys.farmSubdivisionsRoot(farmId), year ?? null] as const,
  // Both QU reads bake a discriminator (params / year) into the key, so — farmSubdivisions
  // precedent — busting every variant needs a root.
  quantificationUnitsRoot: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'quantificationUnits'] as const,
  quantificationUnits: (farmId: string | undefined, params?: unknown) =>
    [...queryKeys.quantificationUnitsRoot(farmId), params ?? null] as const,
  // Farm-scoped rather than opaque-query-keyed (metricEvents precedent): an opaque key would
  // strand another farm's hints past a farm switch.
  quHintsRoot: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'quHints'] as const,
  quHints: (farmId: string | undefined, year?: string) =>
    [...queryKeys.quHintsRoot(farmId), year ?? null] as const,
  projectArea: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'projectArea'] as const,
  socSamples: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'socSamples'] as const,
  // The bbox is part of the response identity: the soils read is an intersection against it,
  // so a perimeter edit must land on a different cache entry rather than reuse the old answer.
  soils: (farmId: string | undefined, bboxKey?: string) =>
    [...queryKeys.farm(farmId), 'soils', bboxKey ?? null] as const,
  exclusionAreas: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'exclusionAreas'] as const,
  samplingAreas: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'samplingAreas'] as const,
  // Nested under samplingAreas on purpose: the effective areas are recomputed from the
  // strata, so every strata invalidation must cascade into this derived read.
  effectiveAreas: (farmId: string | undefined) =>
    [...queryKeys.samplingAreas(farmId), 'effectiveAreas'] as const,
  otherPolygons: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'otherPolygons'] as const,
  otherSites: (farmId: string | undefined) => [...queryKeys.farm(farmId), 'otherSites'] as const,
  // overrideEdition is part of the response identity: the backend relaxes the edition
  // precondition on it, so the two variants are different answers for the same farm.
  perimeterPreconditions: (farmId: string | undefined, overrideEdition: boolean) =>
    [...queryKeys.farm(farmId), 'perimeterPreconditions', overrideEdition] as const,
  // Program-keyed reference data (programMonitoringPeriods precedent): farms of the
  // same program share the cached types.
  exclusionAreaTypes: (programId?: number) => ['exclusionAreaTypes', programId] as const,
  // Not program-scoped: every table reached through this key is global. RefExclusionAreaType is
  // the one with a programIds column, and it keeps the program-keyed entry above.
  refData: (key: string | undefined) => ['refData', key] as const,
  // Flat root on purpose: the key is an opaque Spaces prefix, and no farm write touches S3.
  objectStoreFiles: (dir: string | undefined) => ['objectStoreFiles', dir] as const,
  attestationAreas: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'attestation-areas'] as const,
  otbnAreas: (farmId: string | undefined, query?: unknown) =>
    [...queryKeys.farm(farmId), 'otbn', query ?? null] as const,
  eligibilityCheck: (farmId: string | undefined, query?: unknown) =>
    [...queryKeys.farm(farmId), 'eligibility-check', query ?? null] as const,
  // query ?? null keeps the two useCarbonInstances consumers (FarmMapping's yearId-scoped
  // read and Attestation's programMonitoringPeriodId+cumulative read) and the grouped hook
  // (no query) on distinct cache entries for the same farm.
  carbonInstances: (farmId: string | undefined, query?: unknown) =>
    [...queryKeys.farm(farmId), 'carbonInstances', query ?? null] as const,
  blockedMonitoringPeriods: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'blockedMonitoringPeriods'] as const,
  programMonitoringPeriods: (programId?: string | number) =>
    ['programMonitoringPeriods', programId] as const,
  // Bust all exclusion-area observations (lists + comments) with
  // queryKeys.exclusionAreaObservationsRoot. Intentionally not farm-rooted: an observation's
  // identity is its exclusion area, and the API never exposes them by farm — only the
  // unresolved count below is farm-scoped.
  exclusionAreaObservationsRoot: ['exclusionAreaObservations'] as const,
  exclusionAreaObservations: (exclusionAreaId: string | undefined) =>
    [...queryKeys.exclusionAreaObservationsRoot, 'by-area', exclusionAreaId] as const,
  exclusionAreaObservationComments: (observationId: string | undefined) =>
    [...queryKeys.exclusionAreaObservationsRoot, 'detail', observationId, 'comments'] as const,
  unresolvedObservationsCount: (farmId: string | undefined) =>
    [...queryKeys.farm(farmId), 'exclusionAreaObservations', 'unresolved-count'] as const,
  // Bust all form definitions (single + list) with queryKeys.formDefinitionsRoot.
  // Intentionally not farm-rooted: a definition's identity is its id / the programConfigId,
  // not the currently selected farm — two farms on the same program share cached definitions.
  formDefinitionsRoot: ['formDefinitions'] as const,
  formDefinition: (id: string | undefined) =>
    [...queryKeys.formDefinitionsRoot, 'detail', id] as const,
  formDefinitions: (programConfigId: string | undefined) =>
    [...queryKeys.formDefinitionsRoot, 'list', programConfigId] as const,
  // Not farm-rooted: report templates are static per-deploy files shared by every farm.
  reportTemplates: () => ['reportTemplates'] as const,
  // Not farm-rooted: a global reference table, so farm invalidation must not refetch it.
  monitoringWorkflows: () => ['monitoringWorkflows'] as const,
  // Not farm-rooted: this is the farm collection itself (farms precedent above).
  assignedFarmsRoot: ['assignedFarms'] as const,
  // Normalized so a blank search or an unset filter shares the cache entry of its equivalent.
  assignedFarms: (query: {
    search?: string
    programId?: number
    hubId?: string
    contractStatus?: 'signed' | 'terminating' | 'terminated'
    page: number
    size: number
    sort: `${'name' | 'program' | 'hub'}:${'asc' | 'desc'}`
  }) =>
    [
      ...queryKeys.assignedFarmsRoot,
      {
        search: query.search?.trim() || undefined,
        programId: query.programId ?? null,
        hubId: query.hubId ?? null,
        contractStatus: query.contractStatus ?? null,
        page: query.page,
        size: query.size,
        sort: query.sort,
      },
    ] as const,
  farmFilterOptions: () => ['farmFilterOptions'] as const,
  // Not farm-rooted: getAvailableProgramMonitoringPeriods has no farm, and the farm-rooted entity
  // reads underneath own the invalidations.
  fieldOptions: (
    getterFunction: string | undefined,
    farmId: string | undefined,
    sourceValues: unknown,
  ) => ['fieldOptions', getterFunction, farmId ?? null, sourceValues ?? null] as const,
}
